import useReveal from "@/hooks/useReveal";
import { CalendarRange, Users, BrainCircuit, Receipt, Globe, LayoutDashboard } from "lucide-react";

const features = [
  {
    icon: CalendarRange,
    title: "Calendar and scheduling",
    desc: "Day, week, and month views with multi-practitioner support. Drag to reschedule, colour-coded by type, and synced across the practice.",
    id: "calendar",
  },
  {
    icon: Users,
    title: "Patient records",
    desc: "Full patient profiles with visit history, clinical notes, uploaded documents, and contact details. Everything accessible from one record.",
    id: "patients",
  },
  {
    icon: BrainCircuit,
    title: "AI consultation notes",
    desc: "Record or upload a session, generate a transcript, and get a structured clinical note drafted automatically. Review, edit, and approve before saving.",
    id: "ai-notes",
  },
  {
    icon: Receipt,
    title: "Invoices and payments",
    desc: "Auto-generate invoices linked to appointments. Track payment status, send reminders, and keep billing tied to patient records.",
    id: "invoices",
  },
  {
    icon: Globe,
    title: "Online booking",
    desc: "A branded booking page patients can use directly. Slots sync with your calendar in real time, reducing no-shows and admin.",
    id: "booking",
  },
  {
    icon: LayoutDashboard,
    title: "Clinic dashboard",
    desc: "At-a-glance view of today's appointments, outstanding invoices, recent patient activity, and practice-level performance metrics.",
    id: "dashboard",
  },
];

const FeatureGridSection = () => {
  const [ref, isVisible] = useReveal();

  return (
    <section
      ref={ref}
      data-testid="feature-grid-section"
      className="py-20 md:py-32"
      style={{ backgroundColor: "#F3F2EE" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className={`text-center mb-16 reveal-section ${isVisible ? "visible" : ""}`}>
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-[#205B5A] mb-5">
            Product
          </p>
          <h2
            data-testid="feature-grid-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight leading-tight text-[#1A1B1A] max-w-2xl mx-auto"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Everything clinics need, in one connected system
          </h2>
          <p className="text-base md:text-lg text-[#4A4B4A] leading-relaxed max-w-xl mx-auto mt-5">
            Each feature is built to work together, so clinics don't need separate tools for scheduling, records, billing, and documentation.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, i) => (
            <div
              key={feature.id}
              data-testid={`feature-card-${feature.id}`}
              className={`lf-card bg-white rounded-3xl border border-[#1A1B1A]/[0.05] p-8 md:p-10 shadow-sm reveal-section ${isVisible ? "visible" : ""}`}
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              <div className="w-11 h-11 rounded-xl bg-[#205B5A]/[0.08] flex items-center justify-center mb-6">
                <feature.icon className="w-5 h-5 text-[#205B5A]" strokeWidth={1.5} />
              </div>
              <h3
                className="text-lg md:text-xl font-medium tracking-tight text-[#1A1B1A] mb-3"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                {feature.title}
              </h3>
              <p className="text-sm md:text-base text-[#4A4B4A] leading-relaxed">
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeatureGridSection;
